<!--
  PlacementSizeApprovalDialog -- a placement's new size is not approved by
  the store's client (Jake, 2026-10-27):

    "If the size change requires an approval, show this in a modal dialog,
     along with the same distributor-approval-override affordance we have in
     the embroidery upload dialog in the decoration library."
    "If this requires a new digitization and approval, warn the user in a
     modal with the option to keep the digitized/approved dimensions used in
     the previous placement location, or to override."

  Approval is per SIZE (ADR 0067): every size of a design is its own
  decoration, approved by each client on its own. So the dialog opens only
  when the size the user is moving to has no approval from the store's
  client (GET /placements/<id>/sizing -> candidate.needs_approval).

  Choices (each emitted once; the host, usePlacementSizeChange, writes):
    keep     A move only: move, keep the current (approved) size. Disabled
             when that size is larger than the new location allows.
    proceed  A store that is not live only: use the size now; it goes to
             the client for approval with the store.
    override The decoration library's approve-on-behalf
             (SizeApprovalOverride): a reason and a REQUIRED internal note,
             the same wording, the same per-size
             POST /decorations/<id>/approve-on-behalf. Only for a caller
             holding distributor.decoration_library.override.
    cancel   Change nothing (also the scrim and Esc).

  Presentational apart from the bypass-reason list it loads when the
  override is offered (as SizeApprovalOverride does). The host owns the
  open state: it closes the dialog once a choice's write lands, and leaves
  it open with ``error`` when the write is refused.
-->
<template>
    <v-dialog
        :model-value="modelValue"
        max-width="520"
        :persistent="busy"
        @update:model-value="onUpdate"
    >
        <v-card
            v-if="check"
            data-testid="placement-size-approval-dialog"
        >
            <v-card-title>Approval needed for this size</v-card-title>
            <v-card-text>
                <p
                    class="psad-lede"
                    data-testid="placement-size-approval-message"
                >
                    <template v-if="isMove">
                        At {{ locationLabel }} this design's size is
                        {{ newSize }}.
                    </template>
                    {{ clientName }} hasn't approved this design at
                    {{ newSize }}.
                    <template v-if="live">
                        This store is live, so the placement keeps its
                        current size until {{ clientName }} approves the new
                        one.
                    </template>
                    <template v-else>
                        It needs their approval before this store opens.
                    </template>
                </p>
                <p
                    v-if="check.candidate.may_need_digitizing"
                    class="psad-cost"
                    data-testid="placement-size-approval-digitizing"
                >
                    Embroidery: a new size may need digitizing, a paid
                    art-prep step.
                </p>

                <div
                    v-if="check.can_override"
                    class="psad-override"
                >
                    <p class="psad-override-copy">
                        Approve {{ newSize }} on behalf of
                        <strong>{{ clientName }}</strong>? It counts as the
                        client's approval, including for opening stores, and
                        it is recorded as approved by you, not by the client.
                    </p>
                    <div
                        v-if="reasonsLoading"
                        class="psad-muted"
                    >
                        Loading reasons...
                    </div>
                    <ReasonPicker
                        v-else
                        v-model="reasonId"
                        v-model:notes="notes"
                        :reasons="reasons"
                        :notes-required="true"
                        :confirm-blocked="busy"
                        notes-help="Internal: only your team sees this note. Your client sees the reason you pick."
                        confirm-label="Approve on behalf of client"
                        @confirm="onOverride"
                        @cancel="choose('cancel')"
                    />
                </div>
                <p
                    v-else
                    class="psad-muted"
                    data-testid="placement-size-approval-no-override"
                >
                    Approving on a client's behalf needs the decoration
                    library's override permission.
                </p>

                <p
                    v-if="isMove && !check.current.fits"
                    class="psad-muted"
                    data-testid="placement-size-approval-keep-too-big"
                >
                    The current size, {{ currentSize }}, is larger than
                    {{ locationLabel }} allows.
                </p>
                <p
                    v-if="error || reasonsError"
                    class="psad-error"
                    role="alert"
                    data-testid="placement-size-approval-error"
                >
                    {{ error || reasonsError }}
                </p>
            </v-card-text>
            <v-card-actions class="psad-actions">
                <v-btn
                    variant="text"
                    data-testid="placement-size-approval-cancel"
                    :disabled="busy"
                    @click="choose('cancel')"
                >
                    Cancel
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="!live"
                    variant="text"
                    data-testid="placement-size-approval-proceed"
                    :disabled="busy"
                    @click="choose('proceed')"
                >
                    Use {{ newSize }}, approve later
                </v-btn>
                <v-btn
                    v-if="isMove"
                    color="primary"
                    variant="flat"
                    data-testid="placement-size-approval-keep"
                    :disabled="busy || !check.current.fits"
                    @click="choose('keep')"
                >
                    Keep {{ currentSize }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import api from "@/services/api";
import ReasonPicker from "@/components/ReasonPicker.vue";
import { formatSize } from "@/pages/distributor/store-editor/StoreProducts/Editor/usePlacementSizeChange";

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    /** GET /placements/<id>/sizing for the size in question. */
    check: { type: Object, default: null },
    /** The size comes from a location change (offers keep). */
    isMove: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    error: { type: String, default: "" },
});
const emit = defineEmits(["keep", "proceed", "override", "cancel"]);

const reasons = ref([]);
const reasonsLoading = ref(false);
const reasonsError = ref("");
const reasonId = ref(null);
const notes = ref("");

const clientName = computed(() => props.check?.store?.client?.name || "The client");
const live = computed(() => Boolean(props.check?.store?.live));
const locationLabel = computed(() => props.check?.location?.label || "this location");
const newSize = computed(() =>
    formatSize(props.check?.candidate?.width_in, props.check?.candidate?.height_in),
);
const currentSize = computed(() =>
    formatSize(props.check?.current?.width_in, props.check?.current?.height_in),
);

async function loadReasons() {
    if (reasons.value.length || reasonsLoading.value) return;
    reasonsLoading.value = true;
    reasonsError.value = "";
    try {
        const resp = await api.get("/bypass_reasons/");
        const rows = resp?.data?.data;
        reasons.value = Array.isArray(rows)
            ? rows
                .map((row) => ({ ...(row.attributes || {}), id: Number(row.id) }))
                .filter((r) => r.is_active !== false)
            : [];
        if (!reasons.value.length) reasonsError.value = "No approval reasons are available.";
    } catch (_err) {
        reasonsError.value = "Could not load approval reasons.";
    } finally {
        reasonsLoading.value = false;
    }
}

watch(
    () => [props.modelValue, props.check?.can_override],
    ([open, canOverride]) => {
        if (!open) return;
        reasonId.value = null;
        notes.value = "";
        if (canOverride) loadReasons();
    },
    { immediate: true },
);

// Scrim / Esc. The host closes the dialog by changing ``modelValue`` (which
// Vuetify does not echo back), so any close arriving here is the user's.
function onUpdate(value) {
    if (value || props.busy) return;
    emit("cancel");
}

// keep / proceed / cancel: the host closes the dialog once its write lands
// (or reports the error here), so these do not close it themselves.
function choose(outcome) {
    if (props.busy) return;
    emit(outcome);
}

function onOverride() {
    const text = String(notes.value || "").trim();
    if (reasonId.value == null || !text || props.busy) return;
    emit("override", { reasonId: Number(reasonId.value), notes: text });
}
</script>

<style scoped>
.psad-lede {
    margin: 0 0 8px;
}
.psad-cost {
    margin: 0 0 8px;
    padding: 6px 10px;
    border-radius: var(--tf-radius-md, 6px);
    background: var(--tf-chip-pending-bg, #fef3c7);
    color: var(--tf-chip-pending-fg, #92400e);
    font-size: 13px;
}
.psad-override {
    border: 1px solid var(--tf-border, #e5e7eb);
    border-radius: var(--tf-radius-md, 6px);
    background: var(--tf-surface-alt, #f9fafb);
    padding: 10px 12px;
    margin: 8px 0;
}
.psad-override-copy {
    margin: 0 0 6px;
    font-size: 13px;
}
.psad-muted {
    margin: 6px 0 0;
    font-size: 12.5px;
    color: var(--tf-fg-4, #6b7280);
}
.psad-error {
    margin: 6px 0 0;
    font-size: 12.5px;
    color: var(--tf-chip-error-fg, #b91c1c);
}
.psad-actions {
    flex-wrap: wrap;
    gap: 4px;
}
</style>
